/**
 * Unified Design System
 * Single source of truth for colors, spacing, typography, shadows
 * Now supports light and dark themes
 */
import { type ThemeMode, getThemeColors } from "./theme-constants";

/**
 * Theme-aware color getter
 * Returns colors based on current theme mode
 */
let currentTheme: ThemeMode = "dark";

export function setDesignSystemTheme(theme: ThemeMode) {
  currentTheme = theme;
}

/**
 * Color Palette - Theme Aware
 * These colors adjust based on the current theme
 */
export const colors = {
  accent: "#2dd4ff",
  active: "#22c55e",
  get bgCard() {
    const theme = getThemeColors(currentTheme);
    return theme.background.card;
  },
  get bgHover() {
    return currentTheme === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)";
  },
  get bgPanel() {
    const theme = getThemeColors(currentTheme);
    return theme.background.panel;
  },
  get bgPrimary() {
    const theme = getThemeColors(currentTheme);
    return theme.background.primary;
  },
  get borderDashed() {
    return currentTheme === "dark" ? "#4b5563" : "#9ca3af";
  },
  get borderDashedLight() {
    return currentTheme === "dark" ? "#475569" : "#d1d5db";
  },
  get borderDefault() {
    const theme = getThemeColors(currentTheme);
    return theme.border.primary;
  },
  get borderLight() {
    const theme = getThemeColors(currentTheme);
    return theme.border.secondary;
  },
  danger: "#ef4444",
  edgeActive: "#22c55e",
  edgeDefault: "#4f46e5",
  edgeOffline: "#9ca3af",
  edgeOnline: "#60a5fa",
  edgeQueue: "#38bdf8",
  failed: "#ef4444",
  gradients: {
    nodeActive: ["#22c55e", "#4ade80"],
    nodeOnline: ["#2563eb", "#60a5fa"],
    orchestrator: ["#4c7ff0", "#2dd4ff"],
    queue: ["#f97316", "#facc15"],
    tickets: [
      ["#4c7ff0", "#38bdf8"], // Blue → Cyan
      ["#10b981", "#6ee7b7"], // Green → Light Green
      ["#f97316", "#fbbf24"], // Orange → Amber
      ["#ec4899", "#f472b6"], // Pink → Light Pink
      ["#8b5cf6", "#c4b5fd"], // Purple → Light Purple
      ["#0ea5e9", "#38bdf8"], // Sky → Cyan
      ["#14b8a6", "#5eead4"], // Teal → Light Teal
    ],
  },
  idle: "#60a5fa",
  info: "#3b82f6",
  offline: "#9ca3af",
  online: "#22c55e",
  primary: "#4c7ff0",
  queued: "#f59e0b",
  running: "#3b82f6",
  secondary: "#38bdf8",
  succeeded: "#10b981",
  success: "#22c55e",
  get textDark() {
    return currentTheme === "dark" ? "#0f1729" : "#111827";
  },
  textError: "#fca5a5",
  get textMuted() {
    const theme = getThemeColors(currentTheme);
    return theme.text.muted;
  },
  get textMutedDark() {
    return currentTheme === "dark" ? "#1f2a48" : "#374151";
  },
  get textPrimary() {
    const theme = getThemeColors(currentTheme);
    return theme.text.primary;
  },
  get textSecondary() {
    const theme = getThemeColors(currentTheme);
    return theme.text.secondary;
  },
  warning: "#f59e0b",
} as const;

/**
 * Spacing Scale
 * Based on 4px base unit
 */
export const spacing = {
  0: "0",
  "2xl": "32px", // 2rem
  "3xl": "48px", // 3rem
  "4xl": "64px", // 4rem
  "5xl": "96px", // 6rem
  lg: "16px", // 1rem
  md: "12px", // 0.75rem
  sm: "8px", // 0.5rem
  xl: "24px", // 1.5rem
  xs: "4px", // 0.25rem
} as const;

/**
 * Typography Scale
 */
export const typography = {
  fontSize: {
    "2xl": "24px",
    "3xl": "30px",
    "4xl": "36px",
    base: "14px",
    lg: "18px",
    md: "16px",
    sm: "12px",
    xl: "20px",
    xs: "11px",
  },
  fontWeight: { bold: 700, medium: 500, normal: 400, semibold: 600 },
  letterSpacing: {
    extraWide: "0.3em",
    normal: "0",
    superWide: "0.4em",
    tight: "-0.025em",
    ultraWide: "0.5em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em",
  },
  lineHeight: { normal: 1.5, relaxed: 1.75, tight: 1.25 },
} as const;

/**
 * Shadow Definitions
 */
export const shadows = {
  "2xl": "0 25px 50px rgba(0, 0, 0, 0.25)",
  base: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
  inner: "inset 0 2px 4px rgba(0, 0, 0, 0.06)",
  lg: "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
  md: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
  node: "0 20px 35px rgba(37, 99, 235, 0.35)",
  nodeOffline: "0 10px 24px rgba(15, 23, 42, 0.25)",
  nodeOnline: "0 18px 32px rgba(15, 23, 42, 0.35)",
  nodeSmall: "0 14px 28px rgba(15, 23, 42, 0.35)",
  nodeTicketed: "0 18px 34px rgba(15, 23, 42, 0.3)",
  nodeUnticketed: "0 12px 26px rgba(15, 23, 42, 0.25)",
  none: "none",
  panel: "0 25px 50px rgba(12, 16, 33, 0.4)",
  sm: "0 1px 2px rgba(0, 0, 0, 0.05)",
  xl: "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
} as const;

/**
 * Border Radius Scale
 */
export const borderRadius = {
  "2xl": "24px",
  "3xl": "32px",
  base: "8px",
  full: "9999px",
  lg: "16px",
  md: "12px",
  none: "0",
  sm: "4px",
  xl: "18px",
} as const;

/**
 * Z-index Scale
 */
export const zIndex = {
  base: 0,
  dropdown: 10,
  fixed: 30,
  modal: 50,
  modalBackdrop: 40,
  popover: 60,
  sticky: 20,
  tooltip: 70,
} as const;

/**
 * Animation Durations
 */
export const duration = {
  base: "300ms",
  fast: "150ms",
  instant: "0ms",
  slow: "500ms",
  slower: "750ms",
} as const;

/**
 * Animation Timing Functions
 */
export const easing = {
  bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
  ease: "ease",
  easeIn: "ease-in",
  easeInOut: "ease-in-out",
  easeOut: "ease-out",
  linear: "linear",
  smooth: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

/**
 * Helper function to create gradient strings
 */
export function createGradient(
  gradientColors: readonly [string, string],
  angle = 135,
): string {
  return `linear-gradient(${angle}deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`;
}

/**
 * Helper function to get ticket gradient by index
 */
export function getTicketGradient(index: number): string {
  const gradientColors =
    colors.gradients.tickets[index % colors.gradients.tickets.length];
  return createGradient(gradientColors);
}
